import { FacetOption } from '@tailwindComponents/CheckboxFacetGroup';
import { Supplier, sourceTypeLabelOf } from './supplierTypes';

export interface SupplierFacetState {
  sourceTypes: Set<string>;
  brands: Set<string>;
  coverage: Set<string>;
}

export const emptySupplierFacetState = (): SupplierFacetState => ({
  sourceTypes: new Set(),
  brands: new Set(),
  coverage: new Set(),
});

export const hasActiveFacets = (facets: SupplierFacetState): boolean =>
  facets.sourceTypes.size > 0 ||
  facets.brands.size > 0 ||
  facets.coverage.size > 0;

const matchesSourceType = (
  supplier: Supplier,
  facets: SupplierFacetState
): boolean =>
  facets.sourceTypes.size === 0 || facets.sourceTypes.has(supplier.sourceType);

const matchesBrands = (
  supplier: Supplier,
  facets: SupplierFacetState
): boolean =>
  facets.brands.size === 0 ||
  supplier.brands.some((brand) => facets.brands.has(brand));

const matchesCoverage = (
  supplier: Supplier,
  facets: SupplierFacetState
): boolean =>
  facets.coverage.size === 0 ||
  supplier.coverage.some((branch) => facets.coverage.has(branch));

export const applyFacets = (
  suppliers: Supplier[],
  facets: SupplierFacetState
): Supplier[] =>
  suppliers.filter(
    (supplier) =>
      matchesSourceType(supplier, facets) &&
      matchesBrands(supplier, facets) &&
      matchesCoverage(supplier, facets)
  );

const countBy = (
  suppliers: Supplier[],
  valuesOf: (supplier: Supplier) => string[]
): Map<string, number> => {
  const counts = new Map<string, number>();
  suppliers.forEach((supplier) => {
    valuesOf(supplier).forEach((value) => {
      if (!value) return;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    });
  });
  return counts;
};

const toOptions = (
  counts: Map<string, number>,
  labelOf: (value: string) => string
): FacetOption[] =>
  [...counts.entries()]
    .map(([value, count]) => ({ value, label: labelOf(value), count }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const deriveSourceTypeOptions = (
  suppliers: Supplier[],
  facets: SupplierFacetState
): FacetOption[] =>
  toOptions(
    countBy(
      suppliers.filter(
        (s) => matchesBrands(s, facets) && matchesCoverage(s, facets)
      ),
      (s) => (s.sourceType ? [s.sourceType] : [])
    ),
    sourceTypeLabelOf
  );

export const deriveBrandOptions = (
  suppliers: Supplier[],
  facets: SupplierFacetState
): FacetOption[] =>
  toOptions(
    countBy(
      suppliers.filter(
        (s) => matchesSourceType(s, facets) && matchesCoverage(s, facets)
      ),
      (s) => s.brands
    ),
    (value) => value
  );

export const deriveCoverageOptions = (
  suppliers: Supplier[],
  facets: SupplierFacetState
): FacetOption[] =>
  toOptions(
    countBy(
      suppliers.filter(
        (s) => matchesSourceType(s, facets) && matchesBrands(s, facets)
      ),
      (s) => s.coverage
    ),
    (value) => value
  );

const toggleIn = (set: Set<string>, value: string): Set<string> => {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
};

export const toggleSourceType = (
  facets: SupplierFacetState,
  value: string
): SupplierFacetState => ({
  ...facets,
  sourceTypes: toggleIn(facets.sourceTypes, value),
});

export const setSingleSourceType = (
  facets: SupplierFacetState,
  value: string | null
): SupplierFacetState => ({
  ...facets,
  sourceTypes: value === null ? new Set() : new Set([value]),
});

export const toggleBrand = (
  facets: SupplierFacetState,
  value: string
): SupplierFacetState => ({
  ...facets,
  brands: toggleIn(facets.brands, value),
});

export const toggleCoverage = (
  facets: SupplierFacetState,
  value: string
): SupplierFacetState => ({
  ...facets,
  coverage: toggleIn(facets.coverage, value),
});
